import { useCustomerPageContext } from "@contexts/customer"
import {
  CUSTOMER_PACKAGE_NAMES,
  CUSTOMER_PERIOD_MODES,
  CustomerPackageName,
  CustomerPeriodMode
} from "@domain/customer/list"
import { Button, FormControl, MenuItem, Select } from "@mui/material"
import styles from "./styles"

const PACKAGE_NAME_KEYS: Record<CustomerPackageName, string> = {
  FreeTrial: "package_name_free_trial",
  Start: "package_name_start",
  Grow: "package_name_grow",
  Scale: "package_name_scale",
  Special: "package_name_special"
}

const PERIOD_MODE_KEYS: Record<CustomerPeriodMode, string> = {
  Monthly: "period_mode_monthly",
  Yearly: "period_mode_yearly",
  Once: "period_mode_once"
}

export default function CustomerFilters() {
  const {
    translate,
    appStatus,
    setAppStatus,
    accountStatus,
    setAccountStatus,
    isEmailVerified,
    setIsEmailVerified,
    packageName,
    setPackageName,
    periodMode,
    setPeriodMode,
    setPagination
  } = useCustomerPageContext()

  const resetPage = () => setPagination?.((prev) => ({ ...prev, page: 0 }))

  const hasActiveFilter =
    appStatus !== undefined ||
    accountStatus !== undefined ||
    isEmailVerified !== undefined ||
    packageName !== undefined ||
    periodMode !== undefined

  const clearFilters = () => {
    setAppStatus(undefined)
    setAccountStatus(undefined)
    setIsEmailVerified(undefined)
    setPackageName(undefined)
    setPeriodMode(undefined)
    resetPage()
  }

  return (
    <>
      <FormControl size="small">
        <Select
          value={packageName ?? ""}
          onChange={(event) => {
            const value = event.target.value as CustomerPackageName | ""
            setPackageName(value === "" ? undefined : value)
            resetPage()
          }}
          displayEmpty
          sx={styles.select}
        >
          <MenuItem value="">{translate("package")}</MenuItem>
          {CUSTOMER_PACKAGE_NAMES.map((name) => (
            <MenuItem key={name} value={name}>
              {translate(PACKAGE_NAME_KEYS[name])}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small">
        <Select
          value={periodMode ?? ""}
          onChange={(event) => {
            const value = event.target.value as CustomerPeriodMode | ""
            setPeriodMode(value === "" ? undefined : value)
            resetPage()
          }}
          displayEmpty
          sx={styles.select}
        >
          <MenuItem value="">{translate("renewal_type")}</MenuItem>
          {CUSTOMER_PERIOD_MODES.map((mode) => (
            <MenuItem key={mode} value={mode}>
              {translate(PERIOD_MODE_KEYS[mode])}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small">
        <Select
          value={appStatus ?? ""}
          onChange={(event) => {
            setAppStatus(event.target.value || undefined)
            resetPage()
          }}
          displayEmpty
          sx={styles.select}
        >
          <MenuItem value="">{translate("app_status")}</MenuItem>
          <MenuItem value="Enabled">{translate("app_status_enabled")}</MenuItem>
          <MenuItem value="Disabled">
            {translate("app_status_disabled")}
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small">
        <Select
          value={
            isEmailVerified === undefined ? "" : isEmailVerified ? "yes" : "no"
          }
          onChange={(event) => {
            const value = event.target.value
            setIsEmailVerified(value === "" ? undefined : value === "yes")
            resetPage()
          }}
          displayEmpty
          sx={styles.select}
        >
          <MenuItem value="">{translate("email_status")}</MenuItem>
          <MenuItem value="yes">{translate("verified")}</MenuItem>
          <MenuItem value="no">{translate("unverified")}</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small">
        <Select
          value={accountStatus ?? ""}
          onChange={(event) => {
            setAccountStatus(event.target.value || undefined)
            resetPage()
          }}
          displayEmpty
          sx={styles.select}
        >
          <MenuItem value="">{translate("account_status")}</MenuItem>
          <MenuItem value="Active">
            {translate("customer_status_active")}
          </MenuItem>
          <MenuItem value="Inactive">
            {translate("customer_status_passive")}
          </MenuItem>
        </Select>
      </FormControl>
      {hasActiveFilter && (
        <Button size="small" onClick={clearFilters} sx={styles.clearButton}>
          {translate("clear_filters")}
        </Button>
      )}
    </>
  )
}
